import React from "react";
import { Button, Popconfirm, Space, Spin, Tooltip } from "antd";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  LoadingOutlined,
  RobotOutlined,
  SmileOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import EmojiPicker from "emoji-picker-react";
import dayjs from "dayjs";
import styled from "styled-components";
import { media } from "../../../../styles/breakpoints";
import DoubleCheckIcon from "../../../../components/shared-components/Icons/DoubleCheckIcon";
import CheckIcon from "../../../../components/shared-components/Icons/CheckIcon";
import CrossIcon from "../../../../components/shared-components/Icons/CrossIcon";
import Utils from "../../../../utils";

const MessageBubble = styled.div`
  margin-bottom: ${(props) => (props.hasReaction ? "32px" : "12px")};
  padding: 8px 12px;
  border-radius: 7.5px;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  word-break: break-word;
  position: relative;
  width: fit-content;
  box-sizing: border-box;

  ${media.mobile`
    max-width: 90%;
  `}

  ${(props) =>
    props.isOwn
      ? `
    background: #dcf8c6;
    color: #303030;
    margin-left: auto;
    border-bottom-right-radius: 2px;
  `
      : `
    background: white;
    color: #303030;
    margin-right: auto;
    border-bottom-left-radius: 2px;
    box-shadow: 0 1px 0.5px rgba(0,0,0,0.13);
  `}
`;

const MessageStatus = styled.div``;

const MessageTime = styled.div`
  font-size: 12px;
  color: #667781;
`;

const ChatImage = styled.img`
  display: block;
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);

  ${media.mobile`
    max-width: 100%;
  `}
`;

const DocumentCard = styled.div`
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  max-width: 320px;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  ${media.mobile`
    max-width: 100%;
  `}
  &:hover {
    border-color: #91caff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }
`;

const FileIconWrap = styled.div`
  width: 40px;
  height: 40px;
  background: #eef5ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DocumentTitle = styled.div`
  font-weight: 600;
  color: #111b21;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${media.mobile`
    max-width: 100%;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
    overflow-wrap: anywhere;
  `}
`;

const ChatAudio = styled.audio`
  display: block;
  max-width: 320px;

  ${media.mobile`
    max-width: 100%;
  `}
`;

const DateSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
  position: relative;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(0, 0, 0, 0.06);
  }
`;

const DateSeparatorText = styled.div`
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  color: #667781;
  font-weight: 500;
  margin: 0 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
`;

const ReactionBadge = styled.div`
  position: absolute;
  bottom: -24px;
  ${(props) => (props.isOwn ? "right: 18px;" : "left: 18px;")}
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 0 8px;
  font-size: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  user-select: none;
  cursor: pointer;

  &:hover {
    transform: scale(1.1);
  }
`;

const ReactionButton = styled(Button)`
  position: absolute;
  top: 50%;
  right: -18px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.4);
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f5f5f5;
  }
`;

const ReplyReference = styled.div`
  padding: 6px 10px;
  margin: -4px -8px 8px -8px;
  background: rgba(0, 0, 0, 0.06);
  border-left: 3px solid ${(props) => (props.isOwn ? "#53bdeb" : "#00a884")};
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const ReplyReferenceText = styled.div`
  font-size: 12px;
  color: #667781;
  margin-bottom: 2px;
  font-weight: 500;
`;

const ReplyReferenceContent = styled.div`
  font-size: 13px;
  color: #303030;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

// Helper function to get preview text for referenced message
function getReplyPreviewText(referencedMessage, t) {
  if (!referencedMessage) return "...";

  if (referencedMessage.messageType === "image") {
    return referencedMessage.messageBody
      ? t("chat_image_placeholder") + " - " + referencedMessage.messageBody
      : t("chat_image_placeholder");
  }
  if (referencedMessage.messageType === "document") {
    return referencedMessage.messageBody
      ? t("chat_document_placeholder") + " - " + referencedMessage.messageBody
      : t("chat_document_placeholder");
  }
  if (referencedMessage.messageType === "audio") {
    return referencedMessage.messageBody
      ? t("chat_audio_placeholder") + " - " + referencedMessage.messageBody
      : t("chat_audio_placeholder");
  }
  if (referencedMessage.messageType === "template") {
    return referencedMessage.template?.name || t("template");
  }

  const body = referencedMessage.messageBody || "";
  return body.length > 60 ? body.substring(0, 60) + "..." : body;
}

function scrollToReferencedMessage(wamid) {
  const element = document.querySelector(`[data-message-wamid="${wamid}"]`);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.style.backgroundColor = "rgba(0, 168, 132, 0.2)";
    setTimeout(() => {
      element.style.backgroundColor = "transparent";
    }, 2000);
  }
}

function fileNameFromUrl(url) {
  try {
    const u = new URL(url);
    const pathname = u.pathname;
    const last = pathname.split("%2F").pop();
    return decodeURIComponent(last || "bestand");
  } catch (e) {
    return "bestand";
  }
}

const MessageItem = React.memo(function MessageItem({
  message,
  isOwn,
  isIncoming,
  showDateSeparator,
  referencedMessage,
  isSending,
  isScheduled,
  isHovered,
  isReactionPickerOpen,
  reactionPickerPlacement,
  sendingReactionMessageId,
  messagesContainerRef,
  t,
  onMouseEnter,
  onMouseLeave,
  onImageClick,
  onFileDownload,
  onCancelScheduled,
  onResend,
  onRemoveReaction,
  onOpenReactionPicker,
  onSendReaction,
  reactionPickerRef,
}) {
  const displayTimestamp = !!message.scheduledAt ? message.scheduledAt : message.createdAt;

  return (
    <div>
      {showDateSeparator && (
        <DateSeparator>
          <DateSeparatorText>{message.dateSeparatorLabel}</DateSeparatorText>
        </DateSeparator>
      )}

      <div data-message-timestamp={message.createdAt} data-message-wamid={message.wamid}>
        <div
          style={{
            position: "relative",
            width: "fit-content",
            maxWidth: "70%",
            marginLeft: isOwn ? "auto" : "0",
            paddingRight: isIncoming ? 24 : 0,
            transition: "background-color 0.3s ease",
            borderRadius: "8px",
          }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <MessageBubble isOwn={isOwn} hasReaction={!!message.reaction?.emoji}>
            {referencedMessage && (
              <ReplyReference isOwn={isOwn} onClick={() => scrollToReferencedMessage(referencedMessage.wamid)}>
                <ReplyReferenceText>{t("reply_to") || "Antwoord op"}</ReplyReferenceText>
                <ReplyReferenceContent>{getReplyPreviewText(referencedMessage, t)}</ReplyReferenceContent>
              </ReplyReference>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "2px 6px" }}>
              {message.messageType === "image" && message.fileDownloadUrl ? (
                <div>
                  <ChatImage
                    src={message.fileDownloadUrl}
                    alt={message.messageBody || "image"}
                    loading="lazy"
                    onClick={onImageClick}
                  />
                  {message.messageBody ? (
                    <div style={{ marginTop: 8 }}>{Utils.parseWhatsAppFormatting(message.messageBody)}</div>
                  ) : null}
                </div>
              ) : message.messageType === "document" && message.fileDownloadUrl ? (
                <div>
                  <DocumentCard
                    onClick={() => onFileDownload(message.fileDownloadUrl, fileNameFromUrl(message.fileDownloadUrl))}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <FileIconWrap>
                        <FileOutlined style={{ fontSize: 22, color: "#1677ff" }} />
                      </FileIconWrap>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <DocumentTitle>{fileNameFromUrl(message.fileDownloadUrl)}</DocumentTitle>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#1677ff" }}>
                          <DownloadOutlined />
                          <span>{t("download")}</span>
                        </div>
                      </div>
                    </div>
                  </DocumentCard>
                  {message.messageBody ? (
                    <div style={{ marginTop: 8 }}>{Utils.parseWhatsAppFormatting(message.messageBody)}</div>
                  ) : null}
                </div>
              ) : message.messageType === "audio" && message.fileDownloadUrl ? (
                <div>
                  <ChatAudio controls preload="none">
                    <source src={message.fileDownloadUrl} type="audio/ogg" />
                    Your browser does not support the audio element.
                  </ChatAudio>
                  {message.messageBody ? (
                    <div style={{ marginTop: 8 }}>{Utils.parseWhatsAppFormatting(message.messageBody)}</div>
                  ) : null}
                </div>
              ) : (
                <div style={{ whiteSpace: "pre-line" }}>{Utils.parseWhatsAppFormatting(message.messageBody)}</div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  alignItems: "center",
                  height: "fit-content",
                  marginTop: isSending ? 0 : 4,
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={dayjs.unix(displayTimestamp).tz("Europe/Amsterdam").format("DD MMM YYYY HH:mm")}>
                  <MessageTime isOwn={isOwn}>
                    {dayjs.unix(displayTimestamp).tz("Europe/Amsterdam").format("HH:mm")}
                  </MessageTime>
                </Tooltip>
                {isOwn && (
                  <MessageStatus>
                    {isScheduled ? (
                      <Space size={6}>
                        <Tooltip
                          title={dayjs.unix(message.scheduledAt).tz("Europe/Amsterdam").format("DD MMM YYYY HH:mm")}
                        >
                          <ClockCircleOutlined style={{ color: "#8c8c8c" }} />
                        </Tooltip>
                        <Popconfirm
                          title={t("cancel") || "Annuleren"}
                          onConfirm={() => onCancelScheduled(message)}
                          okText={t("yes")}
                          cancelText={t("no")}
                        >
                          <Button
                            size="small"
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                            style={{ padding: 0, height: 18, color: "#8c8c8c" }}
                          />
                        </Popconfirm>
                      </Space>
                    ) : !!message.timestamps?.whatsappFailed && message.failureReason ? (
                      <Tooltip
                        title={
                          <span>
                            {t("whatsapp_failure_reason") || "WhatsApp failure reason"}:{" "}
                            <i>"{message.failureReason.title || message.failureReason.details}"</i>
                          </span>
                        }
                      >
                        <div style={{ cursor: "help", display: "flex", alignItems: "center" }}>
                          <WarningOutlined style={{ fontSize: 12, color: "#ff8c00" }} />
                        </div>
                      </Tooltip>
                    ) : !!message.timestamps?.whatsappFailed ? (
                      <Tooltip title={t("message_failed") || "Bericht niet afgeleverd"}>
                        <div style={{ cursor: "help" }}>
                          <CrossIcon size={10} color={"red"} />
                        </div>
                      </Tooltip>
                    ) : !!message.timestamps?.whatsappRead ? (
                      <DoubleCheckIcon size={16} color="#53bdeb" />
                    ) : !!message.timestamps?.whatsappDelivered ? (
                      <DoubleCheckIcon size={16} color="grey" />
                    ) : !!message.timestamps?.whatsappSent ? (
                      <CheckIcon size={16} color={"grey"} />
                    ) : isSending ? (
                      <Spin indicator={<LoadingOutlined spin style={{ fontSize: 12 }} />} />
                    ) : message.isCancelled ? (
                      <Tooltip title={t("cancel") || "Geannuleerd"}>
                        <CrossIcon size={10} color={"grey"} />
                      </Tooltip>
                    ) : (
                      <Popconfirm
                        title={t("message_not_sent_popconfirm_title")}
                        onConfirm={() => onResend(message)}
                        okText={t("yes")}
                        cancelText={t("no")}
                      >
                        <div style={{ cursor: "pointer" }}>
                          <CrossIcon size={10} color={"red"} />
                        </div>
                      </Popconfirm>
                    )}
                  </MessageStatus>
                )}
                {message.senderId === "AI_AGENT" && <RobotOutlined style={{ fontSize: 12, color: "grey" }} />}
              </div>
            </div>

            {message.reaction?.emoji && (
              <ReactionBadge
                isOwn={isOwn}
                onClick={() => {
                  if (isIncoming) onRemoveReaction(message);
                }}
              >
                {message.reaction.emoji}
              </ReactionBadge>
            )}
          </MessageBubble>

          {isHovered && isIncoming && (
            <ReactionButton
              onClick={(e) => onOpenReactionPicker(e, message)}
              disabled={sendingReactionMessageId === message.id}
            >
              {sendingReactionMessageId === message.id ? (
                <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} />} />
              ) : (
                <SmileOutlined style={{ fontSize: 14 }} />
              )}
            </ReactionButton>
          )}

          {isReactionPickerOpen && (
            <div
              ref={reactionPickerRef}
              style={{
                position: "absolute",
                bottom: reactionPickerPlacement === "top" ? "100%" : "auto",
                top: reactionPickerPlacement === "bottom" ? "100%" : "auto",
                left: isOwn ? "auto" : "0",
                right: isOwn ? "0" : "auto",
                zIndex: 1000,
                marginBottom: reactionPickerPlacement === "top" ? "8px" : "0",
                marginTop: reactionPickerPlacement === "bottom" ? "8px" : "0",
              }}
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => onSendReaction(message.id, emojiData.emoji)}
                width={400}
                height={400}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default MessageItem;
